import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../JobPosting/jobs.scss";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getCompanyMembers } from "../../Redux/ActionCreators/settingAction";
import {
  fetchJobById,
  listVideoPanelists,
  listVideoRules,
  createVideoPanelist,
  updateVideoPanelist,
  deleteVideoPanelist,
  createVideoRule,
  updateVideoRule,
  deleteVideoRule,
  createVideoSlot,
  deleteVideoSlot,
  updateJobHiringStageSettings,
} from "../../Redux/ActionCreators/jobsAction";

const VIDEO_STAGE_ID = "ed658e02-1549-4d9e-a735-1f1c1e385f46";
const DURATION_OPTIONS = ["30 minutes", "45 minutes", "60 minutes", "90 minutes", "120 minutes"];
const COUNTRY_CODES = [
  { code: "IN", dialCode: "+91", name: "India" },
  { code: "US", dialCode: "+1", name: "United States" },
  { code: "GB", dialCode: "+44", name: "United Kingdom" },
  { code: "AU", dialCode: "+61", name: "Australia" },
  { code: "CA", dialCode: "+1", name: "Canada" },
  { code: "AE", dialCode: "+971", name: "UAE" },
  { code: "SG", dialCode: "+65", name: "Singapore" },
  { code: "DE", dialCode: "+49", name: "Germany" },
  { code: "FR", dialCode: "+33", name: "France" },
  { code: "NL", dialCode: "+31", name: "Netherlands" },
  { code: "JP", dialCode: "+81", name: "Japan" },
  { code: "CN", dialCode: "+86", name: "China" },
  { code: "BR", dialCode: "+55", name: "Brazil" },
  { code: "ZA", dialCode: "+27", name: "South Africa" },
  { code: "NG", dialCode: "+234", name: "Nigeria" },
  { code: "PK", dialCode: "+92", name: "Pakistan" },
  { code: "MY", dialCode: "+60", name: "Malaysia" },
  { code: "PH", dialCode: "+63", name: "Philippines" },
  { code: "SA", dialCode: "+966", name: "Saudi Arabia" },
  { code: "QA", dialCode: "+974", name: "Qatar" },
];

const parsePhoneNo = (phoneNo) => {
  if (!phoneNo) return { dialCode: "+91", number: "" };
  if (phoneNo.startsWith("+")) {
    const matched = COUNTRY_CODES.slice().sort((a, b) => b.dialCode.length - a.dialCode.length)
      .find((c) => phoneNo.startsWith(c.dialCode));
    if (matched) return { dialCode: matched.dialCode, number: phoneNo.slice(matched.dialCode.length) };
  }
  return { dialCode: "+91", number: phoneNo };
};

function PanelistDialCodeSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          border: "none", borderRight: "1px solid #E4E9F0", padding: "0 8px",
          height: "100%", cursor: "pointer", fontSize: "14px", fontFamily: '"inter", sans-serif',
        }}
      >
        {value} <span style={{ fontSize: "9px", marginTop: "1px" }}>▾</span>
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 2px)", left: 0,
          background: "#fff", border: "1px solid #E4E9F0",
          borderRadius: "6px", zIndex: 300,
          maxHeight: "200px", overflowY: "auto",
          minWidth: "170px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}>
          {COUNTRY_CODES.map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => { onChange(c.dialCode); setOpen(false); }}
              style={{
                display: "block", width: "100%",
                padding: "7px 12px", border: "none",
                background: c.dialCode === value ? "#FFF5F9" : "transparent",
                textAlign: "left", cursor: "pointer",
                fontSize: "13px", color: "#2D3748",
                whiteSpace: "nowrap",
              }}
            >
              {c.dialCode} — {c.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const getTimezoneAbbr = () => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZoneName: "short" }).formatToParts(new Date());
    const tz = parts.find((p) => p.type === "timeZoneName")?.value || "UTC";
    return tz.replace(/[+-]\d{1,2}(:\d{2})?$/, "").trim() || "UTC";
  } catch {
    return "UTC";
  }
};

const formatTime12h = (date) => {
  if (!date) return "";
  return date
    .toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })
    .toLowerCase();
};

const formatDateTimeForApi = (dateTime) => {
  if (!dateTime) return null;
  const y = dateTime.getFullYear();
  const mo = String(dateTime.getMonth() + 1).padStart(2, "0");
  const d = String(dateTime.getDate()).padStart(2, "0");
  const h = String(dateTime.getHours()).padStart(2, "0");
  const mi = String(dateTime.getMinutes()).padStart(2, "0");
  const offset = -dateTime.getTimezoneOffset();
  const sign = offset >= 0 ? "+" : "-";
  const oh = String(Math.floor(Math.abs(offset) / 60)).padStart(2, "0");
  const om = String(Math.abs(offset) % 60).padStart(2, "0");
  return `${y}-${mo}-${d}T${h}:${mi}:00${sign}${oh}:${om}`;
};

const makeEmptyRule = () => ({
  dates: [],
  startDate: null,
  endDate: null,
  slots: [],
  picking: false,
  fromTime: null,
  toTime: null,
});

const VideoScreening = ({ setType, setSnackbar, jobHiringStageId, hiringStageId, jobId: propJobId }) => {
  const dispatch = useDispatch();
  const { jobId: paramsJobId } = useParams();
  const jobId = propJobId || paramsJobId;

  const companyMembers = useSelector((state) => state.setting.companyMembers || []);

  const [loading, setLoading] = useState(false);
  const [gappeoEnabled, setGappeoEnabled] = useState(false);
  const [agentId, setAgentId] = useState(null);
  const [recruiter, setRecruiter] = useState("");
  const [recruiterPanelistId, setRecruiterPanelistId] = useState(null);
  const [panelists, setPanelists] = useState([{ name: "", email: "", dialCode: "+91", number: "", id: null }]);
  const [interviewRules, setInterviewRules] = useState([makeEmptyRule()]);
  const [existingRuleId, setExistingRuleId] = useState(null);
  const [deletedSlotIds, setDeletedSlotIds] = useState([]);
  const [duration, setDuration] = useState("");
  const [expiryDate, setExpiryDate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [enableInstructions, setEnableInstructions] = useState(false);
  const [customInstructions, setCustomInstructions] = useState("");

  const resetState = () => {
    setGappeoEnabled(false);
    setAgentId(null);
    setRecruiter("");
    setRecruiterPanelistId(null);
    setPanelists([{ name: "", email: "", dialCode: "+91", number: "", id: null }]);
    setInterviewRules([makeEmptyRule()]);
    setExistingRuleId(null);
    setDeletedSlotIds([]);
    setDuration("");
    setExpiryDate(null);
    setEnableInstructions(false);
    setCustomInstructions("");
  };

  const initialfetch = async () => {
    if (!jobId) return;
    resetState();
    setLoading(true);
    try {
      const [jobAction, membersAction] = await Promise.all([
        dispatch(fetchJobById(jobId)),
        dispatch(getCompanyMembers("", 100, 1)),
      ]);

      // Hydrate video settings from job data
      if (jobAction) {
        const videoStage = jobAction?.hiring_stages?.find(
          (s) => s.hiring_stage_id === VIDEO_STAGE_ID
        );
        if (videoStage?.data?.agent_id) {
          setAgentId(videoStage.data.agent_id);
        }
        if (videoStage?.data?.video_interview_settings) {
          const settings = videoStage.data.video_interview_settings;
          setGappeoEnabled(settings.enable_ai_bot || false);
          if (settings.interview_duration) {
            setDuration(`${settings.interview_duration} minutes`);
          }
          if (settings.expiry_date) {
            setExpiryDate(new Date(settings.expiry_date));
          }
          if (settings.enable_instructions !== undefined) {
            setEnableInstructions(settings.enable_instructions || false);
          }
          if (settings.custom_instructions !== undefined) {
            setCustomInstructions(settings.custom_instructions || "");
          }
        }

        if (!jobHiringStageId) return;
      }

      if (!jobHiringStageId) return;

      const rawMembers = Array.isArray(membersAction?.payload)
        ? membersAction.payload
        : membersAction?.payload?.data || [];

      const [panelistsRes, rulesRes] = await Promise.all([
        dispatch(listVideoPanelists(jobId, jobHiringStageId)),
        dispatch(listVideoRules(jobId, jobHiringStageId)),
      ]);

      // Hydrate recruiter and panelists
      if (panelistsRes?.success && Array.isArray(panelistsRes.data) && panelistsRes.data.length > 0) {
        const recruiterEntry = panelistsRes.data.find((p) => p.role === "recruiter");
        if (recruiterEntry) {
          setRecruiterPanelistId(recruiterEntry.id || null);
          if (recruiterEntry.user_id) {
            const matchedMember = rawMembers.find(
              (m) => String(m.user_id || m.id) === String(recruiterEntry.user_id)
            );
            if (matchedMember) setRecruiter(String(matchedMember.id));
          }
        }

        const panelistEntries = panelistsRes.data
          .filter((p) => p.role !== "recruiter")
          .sort((a, b) => (a.panelist_index || 0) - (b.panelist_index || 0));

        if (panelistEntries.length > 0) {
          setPanelists(
            panelistEntries.map((p) => {
              const { dialCode, number } = parsePhoneNo(p.phone_no);
              return { name: p.name || "", email: p.email || "", dialCode, number, id: p.id || null };
            })
          );
        }
      }

      // Hydrate rules and slots
      if (rulesRes?.success && Array.isArray(rulesRes.data) && rulesRes.data.length > 0) {
        const firstRule = rulesRes.data[0];
        setExistingRuleId(firstRule.id);

        const allSlots = rulesRes.data.flatMap((rule) =>
          (rule.slots || []).map((slot) => ({ ...slot, ruleId: rule.id }))
        );

        if (allSlots.length > 0) {
          const dateMap = {};
          for (const slot of allSlots) {
            const fromDT = new Date(slot.start_time);
            const toDT = new Date(slot.end_time);
            if (isNaN(fromDT) || isNaN(toDT)) continue;
            const dateKey = `${fromDT.getFullYear()}-${fromDT.getMonth()}-${fromDT.getDate()}`;
            if (!dateMap[dateKey]) {
              dateMap[dateKey] = {
                date: new Date(fromDT.getFullYear(), fromDT.getMonth(), fromDT.getDate()),
                slots: [],
              };
            }
            dateMap[dateKey].slots.push({
              label: `${formatTime12h(fromDT)} – ${formatTime12h(toDT)}`,
              fromTime: fromDT,
              toTime: toDT,
              ids: { [dateKey]: slot.id },
              ruleId: slot.ruleId,
            });
          }
          const hydratedRules = Object.values(dateMap).map(({ date, slots }) => ({
            dates: [date], startDate: date, endDate: date, slots, picking: false, fromTime: null, toTime: null,
          }));
          if (hydratedRules.length > 0) setInterviewRules(hydratedRules);
        }
      }
    } catch (err) {
      console.error("Error in VideoScreening initialfetch:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initialfetch();
    // eslint-disable-next-line
  }, [jobId]);

  // --- Panelist helpers ---
  const addPanelist = () => {
    if (panelists.length < 2) {
      setPanelists([...panelists, { name: "", email: "", mobile: "", id: null }]);
    }
  };

  const removePanelist = (index) => {
    setPanelists(panelists.filter((_, i) => i !== index));
  };

  const updatePanelist = (index, field, value) => {
    const updated = [...panelists];
    updated[index] = { ...updated[index], [field]: value };
    setPanelists(updated);
  };

  // --- Interview rule helpers ---
  const addRule = () => setInterviewRules([...interviewRules, makeEmptyRule()]);

  const removeRule = (index) => {
    if (interviewRules.length > 1) {
      const removedSlots = interviewRules[index].slots.filter((s) => s.ids && Object.keys(s.ids).length > 0);
      if (removedSlots.length > 0) {
        const idsToDelete = removedSlots.flatMap((s) =>
          Object.values(s.ids).filter(Boolean).map((id) => ({ slotId: id, ruleId: s.ruleId || existingRuleId }))
        );
        setDeletedSlotIds((prev) => [...prev, ...idsToDelete]);
      }
      setInterviewRules(interviewRules.filter((_, i) => i !== index));
    }
  };

  const handleRangeChange = (ruleIndex, start, end) => {
    const rule = interviewRules[ruleIndex];
    const newDates = [];

    if (start) {
      if (end) {
        let current = new Date(start);
        current.setHours(0, 0, 0, 0);
        const last = new Date(end);
        last.setHours(0, 0, 0, 0);
        while (current <= last) {
          newDates.push(new Date(current));
          current.setDate(current.getDate() + 1);
        }
      } else {
        const d = new Date(start);
        d.setHours(0, 0, 0, 0);
        newDates.push(d);
      }
    }

    const oldDates = rule.dates || [];

    const removedDates = oldDates.filter((od) =>
      !newDates.some((nd) => nd.getTime() === od.getTime())
    );

    const slotsToDelete = [];
    for (const rd of removedDates) {
      const dateKey = `${rd.getFullYear()}-${rd.getMonth()}-${rd.getDate()}`;
      for (const slot of rule.slots) {
        if (slot.ids && slot.ids[dateKey]) {
          slotsToDelete.push({ slotId: slot.ids[dateKey], ruleId: slot.ruleId || existingRuleId });
          delete slot.ids[dateKey];
        }
      }
    }

    if (slotsToDelete.length > 0) {
      setDeletedSlotIds((prev) => [...prev, ...slotsToDelete]);
    }

    updateRule(ruleIndex, { startDate: start, endDate: end, dates: newDates });
  };

  const updateRule = (index, patch) => {
    const updated = [...interviewRules];
    updated[index] = { ...updated[index], ...patch };
    setInterviewRules(updated);
  };

  const openSlotPicker = (index) => {
    if (!duration) {
      setSnackbar({ open: true, message: "Please select an interview duration before adding slots", severity: "warning" });
      return;
    }
    if (!interviewRules[index].dates || interviewRules[index].dates.length === 0) {
      setSnackbar({ open: true, message: "Please select at least one date before adding slots", severity: "warning" });
      return;
    }
    updateRule(index, { picking: true, fromTime: null, toTime: null });
  };

  const closeSlotPicker = (index) =>
    updateRule(index, { picking: false, fromTime: null, toTime: null });

  const confirmSlot = (index) => {
    const rule = interviewRules[index];
    if (!rule.dates || rule.dates.length === 0 || !rule.fromTime || !rule.toTime) return;
    const fromDT = rule.fromTime;
    const toDT = rule.toTime;
    const slotMinutes = (toDT - fromDT) / (1000 * 60);
    const durationMinutes = parseInt(duration);
    if (slotMinutes < durationMinutes) {
      setSnackbar({ open: true, message: `Slot duration must be at least ${duration} to match the interview duration`, severity: "error" });
      return;
    }
    const newFrom = fromDT.getHours() * 60 + fromDT.getMinutes();
    const newTo = toDT.getHours() * 60 + toDT.getMinutes();
    const overlaps = rule.slots.some((slot) => {
      if (!slot.fromTime || !slot.toTime) return false;
      const eFrom = slot.fromTime.getHours() * 60 + slot.fromTime.getMinutes();
      const eTo = slot.toTime.getHours() * 60 + slot.toTime.getMinutes();
      return newFrom < eTo && newTo > eFrom;
    });
    if (overlaps) {
      setSnackbar({ open: true, message: "This slot overlaps with an existing slot. Please choose a different time.", severity: "error" });
      return;
    }
    updateRule(index, {
      slots: [
        ...rule.slots,
        { label: `${formatTime12h(fromDT)} – ${formatTime12h(toDT)}`, fromTime: fromDT, toTime: toDT, ids: {}, ruleId: null },
      ],
      picking: false,
      fromTime: null,
      toTime: null,
    });
  };

  const removeSlot = (ruleIndex, slotIndex) => {
    const removedSlot = interviewRules[ruleIndex].slots[slotIndex];
    if (removedSlot.ids && Object.keys(removedSlot.ids).length > 0) {
      const idsToDelete = Object.values(removedSlot.ids).filter(Boolean);
      setDeletedSlotIds((prev) => [
        ...prev,
        ...idsToDelete.map((id) => ({ slotId: id, ruleId: removedSlot.ruleId || existingRuleId })),
      ]);
    }
    const updated = [...interviewRules];
    updated[ruleIndex] = {
      ...updated[ruleIndex],
      slots: updated[ruleIndex].slots.filter((_, i) => i !== slotIndex),
    };
    setInterviewRules(updated);
  };

  const getExcludedDates = (currentRuleIndex) =>
    interviewRules
      .filter((r, i) => i !== currentRuleIndex && r.dates)
      .flatMap((r) => r.dates);

  const makeTimeFilter = (ruleIndex) => (time) => {
    const rule = interviewRules[ruleIndex];
    const timeVal = time.getHours() * 60 + time.getMinutes();
    for (const slot of rule.slots) {
      if (!slot.fromTime || !slot.toTime) continue;
      const fromVal = slot.fromTime.getHours() * 60 + slot.fromTime.getMinutes();
      const toVal = slot.toTime.getHours() * 60 + slot.toTime.getMinutes();
      if (timeVal >= fromVal && timeVal < toVal) return false;
    }
    return true;
  };

  const handleDurationChange = (e) => {
    const newDuration = e.target.value;
    setDuration(newDuration);
    if (!newDuration) return;
    const newDurationMinutes = parseInt(newDuration);

    let removedCount = 0;
    const slotsToDelete = [];

    const newRules = interviewRules.map((rule) => {
      const validSlots = [];
      for (const slot of rule.slots) {
        if (!slot.fromTime || !slot.toTime) { validSlots.push(slot); continue; }
        const slotMinutes = (slot.toTime - slot.fromTime) / (1000 * 60);
        if (slotMinutes >= newDurationMinutes) {
          validSlots.push(slot);
        } else {
          removedCount++;
          if (slot.ids) {
            const idsToDelete = Object.values(slot.ids).filter(Boolean);
            slotsToDelete.push(...idsToDelete.map((id) => ({ slotId: id, ruleId: slot.ruleId || existingRuleId })));
          }
        }
      }
      return { ...rule, slots: validSlots };
    });

    if (removedCount > 0) {
      setInterviewRules(newRules);
      if (slotsToDelete.length > 0) setDeletedSlotIds((prev) => [...prev, ...slotsToDelete]);
      setSnackbar({
        open: true,
        message: `${removedCount} slot${removedCount > 1 ? "s were" : " was"} removed because their duration is less than the new interview duration of ${newDuration}`,
        severity: "warning",
      });
    }
  };

  const handleExpiryChange = (date) => {
    setExpiryDate(date);
    if (!date) return;
    const endOfExpiry = new Date(date);
    endOfExpiry.setHours(23, 59, 59, 999);
    let slotsToDelete = [];
    const remainingRules = [];
    for (const r of interviewRules) {
      const validDates = (r.dates || []).filter((d) => d <= endOfExpiry);
      const invalidDates = (r.dates || []).filter((d) => d > endOfExpiry);
      for (const rd of invalidDates) {
        const dateKey = `${rd.getFullYear()}-${rd.getMonth()}-${rd.getDate()}`;
        for (const slot of r.slots) {
          if (slot.ids && slot.ids[dateKey]) {
            slotsToDelete.push({ slotId: slot.ids[dateKey], ruleId: slot.ruleId || existingRuleId });
            delete slot.ids[dateKey];
          }
        }
      }
      if (validDates.length > 0) {
        remainingRules.push({ ...r, dates: validDates });
      }
    }
    if (slotsToDelete.length > 0) {
      setDeletedSlotIds((ids) => [...ids, ...slotsToDelete]);
    }
    setInterviewRules(remainingRules.length > 0 ? remainingRules : [makeEmptyRule()]);
    setSnackbar({
      open: true,
      message: "Interview rules with dates beyond the new expiry date have been removed",
      severity: "warning",
    });
  };

  // --- Validation ---
  const validate = () => {
    if (!gappeoEnabled) return null;
    if (!recruiter) return "Please select a recruiter";
    if (!duration) return "Please select an interview duration";
    for (let i = 0; i < interviewRules.length; i++) {
      if (!interviewRules[i].dates || interviewRules[i].dates.length === 0) {
        return `Interview rule ${i + 1}: Please select at least one date`;
      }
      if (interviewRules[i].slots.length === 0) {
        return `Interview rule ${i + 1}: Please add at least one slot`;
      }
    }
    if (!expiryDate) return "Please select a scheduling link expiry date";
    return null;
  };

  // --- Save ---
  const handleSave = async () => {
    if (!gappeoEnabled) {
      if (customInstructions.trim() && !enableInstructions) {
        setSnackbar({ open: true, message: "Please check 'Use these custom instructions...' to save custom instructions", severity: "warning" });
        return;
      }
      if (!jobHiringStageId) {
        setSnackbar({ open: true, message: "Video interview stage not found for this job", severity: "error" });
        return;
      }
      setSaving(true);
      try {
        // Re-fetch to get all current panelists and rules before deleting
        const [panelistsRes, rulesRes] = await Promise.all([
          dispatch(listVideoPanelists(jobId, jobHiringStageId)),
          dispatch(listVideoRules(jobId, jobHiringStageId)),
        ]);

        // Delete all panelists (recruiter + panel)
        if (panelistsRes?.success && Array.isArray(panelistsRes.data)) {
          for (const panelist of panelistsRes.data) {
            if (panelist.id) {
              await dispatch(deleteVideoPanelist(jobId, jobHiringStageId, panelist.id));
            }
          }
        }

        // Delete all slots then rules
        if (rulesRes?.success && Array.isArray(rulesRes.data)) {
          for (const rule of rulesRes.data) {
            for (const slot of (rule.slots || [])) {
              if (slot.id) {
                await dispatch(deleteVideoSlot(jobId, jobHiringStageId, rule.id, slot.id));
              }
            }
            await dispatch(deleteVideoRule(jobId, jobHiringStageId, rule.id));
          }
        }

        const settingsResult = await dispatch(updateJobHiringStageSettings(jobId, jobHiringStageId, {
          job_id: jobId,
          hiring_stage_id: hiringStageId,
          sequence: null,
          is_active: true,
          data: {
            ...(agentId ? { agent_id: agentId } : {}),
            video_interview_settings: {
              enable_ai_bot: false,
              video_interview_type: enableInstructions && customInstructions.trim() ? "ai_interview" : "hybrid",
              enable_instructions: enableInstructions && customInstructions.trim() ? true : false,
              custom_instructions: enableInstructions && customInstructions.trim() ? customInstructions.trim() : "",
            },
          },
        }));
        if (!settingsResult?.success) {
          setSnackbar({ open: true, message: settingsResult?.message || "Failed to save video settings", severity: "error" });
          return;
        }
        setSnackbar({ open: true, message: "Video interview settings saved successfully", severity: "success" });
        resetState();
        setType("");
      } catch (err) {
        setSnackbar({ open: true, message: "Failed to save video interview settings", severity: "error" });
      } finally {
        setSaving(false);
      }
      return;
    }

    const validationError = validate();
    if (validationError) {
      setSnackbar({ open: true, message: validationError, severity: "error" });
      return;
    }

    setSaving(true);
    try {
      if (!jobHiringStageId) {
        setSnackbar({ open: true, message: "Video interview stage not found for this job", severity: "error" });
        setSaving(false);
        return;
      }

      // Step 1: Create or update recruiter panelist
      const selectedMember = companyMembers.find((m) => String(m.id) === String(recruiter));
      const recruiterPayload = {
        name: selectedMember?.name || "",
        email: selectedMember?.email || "",
        role: "recruiter",
        user_id: selectedMember?.user_id || selectedMember?.id || null,
        phone_no: selectedMember?.phone || selectedMember?.mobile || "",
        is_required: true,
        is_active: true,
      };
      const recruiterResult = recruiterPanelistId
        ? await dispatch(updateVideoPanelist(jobId, jobHiringStageId, recruiterPanelistId, recruiterPayload))
        : await dispatch(createVideoPanelist(jobId, jobHiringStageId, recruiterPayload));
      if (!recruiterResult?.success) {
        setSnackbar({ open: true, message: recruiterResult?.message || "Failed to create video settings for recruiter", severity: "error" });
        setSaving(false);
        return;
      }

      // Step 1b: Create or update each panelist (skip if all fields empty)
      for (let i = 0; i < panelists.length; i++) {
        const p = panelists[i];
        if (!p.name.trim() && !p.email.trim() && !p.number.trim() && !p.id) continue;
        const panelistPayload = {
          name: p.name,
          email: p.email,
          role: "panelist",
          user_id: null,
          phone_no: `${p.dialCode}${p.number}`,
          panelist_index: i + 1,
          is_required: true,
          is_active: true,
        };
        const pResult = p.id
          ? await dispatch(updateVideoPanelist(jobId, jobHiringStageId, p.id, panelistPayload))
          : await dispatch(createVideoPanelist(jobId, jobHiringStageId, panelistPayload));
        if (!pResult?.success) {
          setSnackbar({ open: true, message: pResult?.message || `Failed to create video settings for panelist ${i + 1}`, severity: "error" });
          setSaving(false);
          return;
        }
      }

      // Step 2: Create or update rule
      const timezone = getTimezoneAbbr();
      let ruleId = existingRuleId;
      if (existingRuleId) {
        const ruleResult = await dispatch(updateVideoRule(jobId, jobHiringStageId, existingRuleId, { timezone, is_active: true }));
        if (!ruleResult?.success) {
          setSnackbar({ open: true, message: ruleResult?.message || "Failed to update video settings for rules", severity: "error" });
          setSaving(false);
          return;
        }
      } else {
        const ruleResult = await dispatch(createVideoRule(jobId, jobHiringStageId, { timezone, is_active: true }));
        if (!ruleResult?.success) {
          setSnackbar({ open: true, message: ruleResult?.message || "Failed to create video settings for rules", severity: "error" });
          setSaving(false);
          return;
        }
        ruleId = ruleResult?.data?.id || ruleResult?.data?.rule_id;
      }

      // Step 3a: Delete removed slots first
      for (const { slotId, ruleId: slotRuleId } of deletedSlotIds) {
        const delResult = await dispatch(deleteVideoSlot(jobId, jobHiringStageId, slotRuleId || ruleId, slotId));
        if (!delResult?.success) {
          setSnackbar({ open: true, message: delResult?.message || "Failed to delete slot", severity: "error" });
          setSaving(false);
          return;
        }
      }

      // Step 3b: Post only new slots (those without an existing API id for the specific date)
      const newSlotsPayload = [];
      for (const rule of interviewRules) {
        for (const date of rule.dates || []) {
          const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
          for (const slot of rule.slots) {
            const existingId = slot.ids && slot.ids[dateKey];
            if (!existingId) {
              const slotFrom = new Date(date);
              slotFrom.setHours(slot.fromTime.getHours(), slot.fromTime.getMinutes(), 0, 0);
              const slotTo = new Date(date);
              slotTo.setHours(slot.toTime.getHours(), slot.toTime.getMinutes(), 0, 0);

              newSlotsPayload.push({
                start_time: formatDateTimeForApi(slotFrom),
                end_time: formatDateTimeForApi(slotTo),
                is_active: true,
              });
            }
          }
        }
      }

      if (newSlotsPayload.length > 0) {
        const slotResult = await dispatch(createVideoSlot(jobId, jobHiringStageId, ruleId, newSlotsPayload));
        if (!slotResult?.success) {
          setSnackbar({ open: true, message: slotResult?.message || "Failed to create video settings for slots", severity: "error" });
          setSaving(false);
          return;
        }
      }

      // Step 4: Update video interview settings on the hiring stage
      const interviewDuration = parseInt(duration);
      const expiryISO = expiryDate
        ? (() => { const d = new Date(expiryDate); d.setHours(23, 59, 59, 0); return d.toISOString(); })()
        : null;

      const settingsResult = await dispatch(updateJobHiringStageSettings(jobId, jobHiringStageId, {
        job_id: jobId,
        hiring_stage_id: hiringStageId,
        sequence: null,
        is_active: true,
        data: {
          ...(agentId ? { agent_id: agentId } : {}),
          video_interview_settings: {
            enable_ai_bot: gappeoEnabled,
            interview_duration: interviewDuration,
            video_interview_type: "hybrid",
            expiry_date: expiryISO,
            enable_instructions: false,
            custom_instructions: customInstructions,
          },
        },
      }));
      if (!settingsResult?.success) {
        setSnackbar({ open: true, message: settingsResult?.message || "Failed to create video settings", severity: "error" });
        setSaving(false);
        return;
      }

      setSnackbar({ open: true, message: "Video interview settings saved successfully", severity: "success" });
      resetState();
      setType("");
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to save video interview settings", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="job-prescreening">
      <div className="job-settings-head">
        <img
          src="/images/back-arrow-black.svg"
          alt="back"
          width={32}
          height={32}
          onClick={() => { resetState(); setType(""); }}
          className="vs-back-btn"
        />
        <div className="job-settings-head-title d-flex align-items-center">
          <h3 className="vs-page-title">Video interview Settings</h3>
        </div>
      </div>

      <div className="job-settings-body">
        {loading ? (
          <div
            className="loading-overlay"
            style={{ position: "relative", marginTop: 16, alignItems: "start", height: "32px" }}
          >
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="job-settings-form">

            {/* Enable Gappeo Agent */}
            <div className="vs-gappeo-card">
              <div className="vs-gappeo-card-info">
                <h5>Enable Gappeo agent</h5>
                <p>AI observer joins as a silent assistant</p>
              </div>
              <label className={`toggle-switch toggle-pink${gappeoEnabled ? " checked" : ""}`}>
                <input
                  type="checkbox"
                  checked={gappeoEnabled}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setGappeoEnabled(enabled);
                    if (!enabled && customInstructions.trim()) {
                      setEnableInstructions(true);
                    }
                  }}
                />
                <span className="toggle-slider" />
              </label>
            </div>

            {gappeoEnabled && (
              <>
                {/* Select Recruiter */}
                <div className="job-settings-form-header flex-column vs-field-gap">
                  <label>Select recruiter</label>
                  <select
                    value={recruiter}
                    onChange={(e) => setRecruiter(e.target.value)}
                    className="vs-recruiter-select"
                  >
                    <option value="">Select recruiter</option>
                    {companyMembers.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}{m.email ? ` (${m.email})` : ""}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Panelists */}
                <div className="job-settings-form-question">
                  <span className="vs-section-label">Panelists</span>

                  {panelists.map((p, index) => (
                    <div key={index} className="vs-panelist-row">
                      <input
                        type="text"
                        placeholder="Name"
                        value={p.name}
                        onChange={(e) => updatePanelist(index, "name", e.target.value)}
                        className="vs-input-name"
                      />
                      <input
                        type="email"
                        placeholder="Email"
                        value={p.email}
                        onChange={(e) => updatePanelist(index, "email", e.target.value)}
                        className="vs-input-email"
                      />
                      <div style={{
                        display: "flex", alignItems: "stretch",
                        border: "1px solid #E4E9F0", height: '36px'
                      }}>
                        <PanelistDialCodeSelect
                          value={p.dialCode}
                          onChange={(dialCode) => updatePanelist(index, "dialCode", dialCode)}
                        />
                        <input
                          type="tel"
                          placeholder="Mobile number"
                          value={p.number}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^\d\s\-()]/g, "");
                            updatePanelist(index, "number", val);
                          }}
                        />
                      </div>
                      {panelists.length > 1 && (
                        <img
                          src="/images/cross-pink.svg"
                          alt="remove"
                          width={14}
                          height={14}
                          className="vs-remove-icon"
                          onClick={() => removePanelist(index)}
                        />
                      )}
                    </div>
                  ))}

                  {panelists.length < 2 && (
                    <button type="button" className="logistic-btn vs-add-btn" onClick={addPanelist}>
                      + Add panelist
                    </button>
                  )}
                </div>

                {/* Duration + Expiry */}
                <div className="vs-duration-row">
                  <div className="vs-duration-field job-settings-form-header" style={{ gap: "unset" }}>
                    <label style={{ marginBottom: "6px" }}>Interview duration</label>
                    <select value={duration} onChange={handleDurationChange}>
                      <option value="">Select duration</option>
                      {DURATION_OPTIONS.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                  <div className="vs-duration-field job-settings-form-header" style={{ gap: "unset" }}>
                    <label style={{ marginBottom: "6px" }}>Scheduling link expiry</label>
                    <DatePicker
                      selected={expiryDate}
                      onChange={handleExpiryChange}
                      dateFormat="dd-MM-yyyy"
                      placeholderText="Select expiry date"
                      minDate={new Date()}
                      className="vs-date-input"
                      style={{ width: "unset", height: "unset" }}
                    />
                  </div>
                </div>

                {/* Interview Time Rules */}
                <div className="">
                  <span className="vs-section-label">Interview Time Rules</span>

                  {interviewRules.map((rule, ruleIndex) => (
                    <div key={ruleIndex} className="vs-time-rule-row job-settings-form-header">
                      <DatePicker
                        selectsRange
                        startDate={rule.startDate}
                        endDate={rule.endDate}
                        onChange={(update) => handleRangeChange(ruleIndex, update[0], update[1])}
                        dateFormat="dd MMM yyyy"
                        placeholderText="Select date range"
                        minDate={new Date()}
                        maxDate={expiryDate || undefined}
                        excludeDates={getExcludedDates(ruleIndex)}
                        className="vs-day-select form-control"
                        style={{ maxWidth: "260px", minWidth: "260px", height: "36px" }}
                      />

                      <div className="vs-slot-pills">
                        {rule.slots.map((slot, slotIndex) => (
                          <span key={slotIndex} className="vs-slot-pill">
                            {slot.label}
                            <span
                              className="vs-slot-pill-remove"
                              onClick={() => removeSlot(ruleIndex, slotIndex)}
                            >
                              ×
                            </span>
                          </span>
                        ))}

                        {rule.picking ? (
                          <div className="vs-slot-adder">
                            <DatePicker
                              selected={rule.fromTime}
                              onChange={(date) => updateRule(ruleIndex, { fromTime: date })}
                              onChangeRaw={(e) => e.preventDefault()}
                              showTimeSelect
                              showTimeSelectOnly
                              timeIntervals={15}
                              timeCaption="From"
                              dateFormat="h:mm aa"
                              placeholderText="Start time"
                              className="vs-time-input form-control"
                              filterTime={makeTimeFilter(ruleIndex)}
                              style={{ width: "150px", height: "36px" }}
                            />
                            <span className="vs-time-sep">–</span>
                            <DatePicker
                              selected={rule.toTime}
                              onChange={(date) => updateRule(ruleIndex, { toTime: date })}
                              onChangeRaw={(e) => e.preventDefault()}
                              showTimeSelect
                              showTimeSelectOnly
                              timeIntervals={15}
                              timeCaption="To"
                              dateFormat="h:mm aa"
                              placeholderText="End time"
                              className="vs-time-input form-control"
                              minTime={rule.fromTime || new Date(new Date().setHours(0, 0))}
                              maxTime={new Date(new Date().setHours(23, 59))}
                              filterTime={makeTimeFilter(ruleIndex)}
                              style={{ width: "150px", height: "36px" }}
                            />
                            <button
                              type="button"
                              className="vs-slot-confirm-btn"
                              onClick={() => confirmSlot(ruleIndex)}
                              disabled={!rule.fromTime || !rule.toTime}
                            >
                              Add
                            </button>
                            <button
                              type="button"
                              className="vs-slot-cancel-btn"
                              onClick={() => closeSlotPicker(ruleIndex)}
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="vs-add-slot-btn"
                            onClick={() => openSlotPicker(ruleIndex)}
                          >
                            + Add slot
                          </button>
                        )}
                      </div>

                      {interviewRules.length > 1 && (
                        <img
                          src="/images/cross-pink.svg"
                          alt="remove rule"
                          width={14}
                          height={14}
                          className="vs-remove-icon"
                          onClick={() => removeRule(ruleIndex)}
                        />
                      )}
                    </div>
                  ))}

                  <button type="button" className="logistic-btn vs-add-btn" onClick={addRule}>
                    + Add rule
                  </button>
                </div>

              </>
            )}

            {/* Custom Instructions — shown only when Gappeo agent is disabled */}
            {!gappeoEnabled && (
              <div className="job-settings-form-question">
                <div className="d-flex align-items-center">
                  <h5>Custom Instructions</h5>
                </div>
                <p>Note: AI Assistant will use these instructions during the video interview</p>
                <textarea
                  rows="6"
                  placeholder="Enter custom instructions here..."
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                />
                <label>
                  <input
                    type="checkbox"
                    checked={enableInstructions}
                    onChange={(e) => setEnableInstructions(e.target.checked)}
                    style={{ width: "unset", height: "unset" }}
                  />{" "}
                  Use these custom instructions as part of AI Video Interview
                </label>
              </div>
            )}

            {/* Action Buttons */}
            <div className="d-flex gap-4 align-items-center">
              <button
                className="save-btn"
                type="button"
                onClick={handleSave}
                disabled={saving}
                style={{ opacity: saving ? 0.6 : 1, cursor: saving ? "not-allowed" : "pointer" }}
              >
                {saving ? "Saving..." : "Save settings"}
              </button>
              <h6 className="cursor-pointer cancle-txt" onClick={() => { resetState(); setType(""); }}>
                Cancel
              </h6>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default VideoScreening;
